import { Domain, getRepository } from '@things-factory/shell'

/**
 * 멀티테넌트 도메인 다단계 상속 헬퍼.
 *
 * SYSTEM → PROJECT-TEMPLATE → project 처럼 계층이 깊어질 수 있으므로
 * 현재 도메인 부터 루트(부모가 없는 도메인)까지의 ancestor id 를
 * "가까운 순서대로" 반환한다. (closest-first)
 *
 * - 사용처: 단일 fetch 의 `In([...])`, list 조회의 `andWhere('domain IN (:...ids)')`,
 *   그리고 closest-wins dedup 의 우선순위 키.
 *
 * 깊이는 보통 2~3 이지만 순환 방지 + 최대 깊이(10) 안전장치를 둔다.
 */
const MAX_ANCESTOR_DEPTH = 10

export async function getDomainIdsWithAncestors(domain: Domain): Promise<string[]> {
  const ids: string[] = [domain.id]
  const seen = new Set<string>([domain.id])

  let parentId: string | undefined = domain.parentId
  let depth = 0

  while (parentId && !seen.has(parentId) && depth < MAX_ANCESTOR_DEPTH) {
    seen.add(parentId)
    ids.push(parentId)

    const parent = await getRepository(Domain).findOne({ where: { id: parentId } })
    parentId = parent?.parentId
    depth++
  }

  return ids
}

/**
 * "가까운 도메인 우선(override)" 의미 구현.
 * 여러 ancestor 도메인의 항목들 중 같은 키(name 등)가 중복 존재하면
 * domainIds 배열에서 더 앞(=가까운 도메인)에 있는 도메인에 속한 항목 1개만 남긴다.
 *
 * - domainIds: closest-first 순서 (getDomainIdsWithAncestors 결과)
 * - keyFn: 중복 판정 키 (예: kpi.name)
 *
 * 정렬은 호출자가 다시 적용해야 한다 (typeorm 의 사전 정렬은 dedup 으로 깨지지 않음 -
 *  같은 키 그룹 내부의 선택만 바뀌고 그룹의 위치는 입력 순서를 유지하므로
 *  안정적이다).
 */
export function dedupByClosestDomain<T extends { domainId?: string }>(
  items: T[],
  domainIds: string[],
  keyFn: (item: T) => string | undefined
): T[] {
  const rankOf = (domainId: string | undefined): number => {
    if (!domainId) return Number.MAX_SAFE_INTEGER
    const idx = domainIds.indexOf(domainId)
    return idx < 0 ? Number.MAX_SAFE_INTEGER : idx
  }

  const byKey = new Map<string, T>()
  const order: string[] = []

  for (const item of items) {
    const key = keyFn(item)
    if (key == null) continue

    const existing = byKey.get(key)
    if (!existing) {
      byKey.set(key, item)
      order.push(key)
    } else if (rankOf(item.domainId) < rankOf(existing.domainId)) {
      byKey.set(key, item)
    }
  }

  return order.map(k => byKey.get(k)!)
}
